import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { PublicHeader, PublicFooter, ArticleCard } from '../components/PublicLayout';
import { searchAPI } from '../lib/api';
import { useEdition } from '../lib/edition-context';
import { Skeleton } from '../components/ui/skeleton';
import { Search as SearchIcon } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function SearchPage() {
  const { edition } = useEdition();
  const editionParam = edition && edition !== 'global' ? edition : undefined;
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(query);

  useEffect(() => {
    if (query) doSearch(query);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, edition]);

  const doSearch = async (q) => {
    setLoading(true);
    try {
      const res = await searchAPI.search(q, { edition: editionParam });
      setResults(res.data.articles || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchInput.trim())}`;
    }
  };

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="flex gap-3 max-w-xl">
              <div className="flex-1 relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5B616E]" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full pl-10 pr-4 py-3 border border-[#E7E0D6] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#B89B5E] text-sm"
                />
              </div>
              <Button type="submit" className="bg-[#121316] text-white hover:bg-[#1B1D22]">Search</Button>
            </div>
          </form>

          {query && (
            <p className="text-sm text-[#5B616E] mb-6">
              {total} result{total !== 1 ? 's' : ''} for "{query}"
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
            </div>
          ) : results.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : query ? (
            <div className="bg-white rounded-xl border border-[#E7E0D6] p-12 text-center text-[#5B616E]">
              <SearchIcon className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
              <p className="text-lg">No results found</p>
              <p className="text-sm mt-2">Try different keywords or browse our categories.</p>
            </div>
          ) : null}
        </div>
      </main>
      <PublicFooter />
    </>
  );
}
